import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { deleteCollection, getUserRecipes, addRecipeToCollection } from '../services/api';
import { PageHeading, Heading, Text, Modal } from '../components/ui';
import SignedImage from '../components/SignedImage';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const CollectionDetail = () => {
    const { collectionId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showAddRecipesModal, setShowAddRecipesModal] = useState(false);
    const [allUserRecipes, setAllUserRecipes] = useState([]);
    const [loadingRecipes, setLoadingRecipes] = useState(false);
    const [addingRecipes, setAddingRecipes] = useState(false);
    const [selectedRecipes, setSelectedRecipes] = useState(new Set());
    
    // Get collection data from navigation state
    const collectionName = location.state?.collectionName || 'Collection';
    const recipes = location.state?.recipes || [];

    // Function to format cooking time
    const formatCookingTime = (minutes) => {
        if (!minutes) return 'No time specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    // Delete collection function
    const handleDeleteCollection = async () => {
        if (!user || !collectionId) return;
        
        try {
            setDeleting(true);
            const token = await user.getIdToken();
            await deleteCollection(collectionId, token);
            
            // Navigate back to recipes page after successful deletion
            navigate('/recipes');
        } catch (err) {
            console.error('Error deleting collection:', err);
            alert(`Failed to delete collection: ${err.message || 'Please try again.'}`);
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    };

    // Fetch all user recipes for adding to collection
    const fetchUserRecipes = async () => {
        if (!user) return;
        
        try {
            setLoadingRecipes(true);
            const token = await user.getIdToken();
            const userRecipes = await getUserRecipes(user.uid, token);
            
            // Filter to show only the most recent version of each recipe chain
            const chainGroups = new Map();
            userRecipes.forEach(recipe => {
                const chainId = recipe.chain_root_id;
                if (!chainGroups.has(chainId) || 
                    new Date(recipe.created_at) > new Date(chainGroups.get(chainId).created_at)) {
                    chainGroups.set(chainId, recipe);
                }
            });
            
            // Get only the most recent versions
            const mostRecentRecipes = Array.from(chainGroups.values());
            
            // Filter out recipes that are already in this collection
            const availableRecipes = mostRecentRecipes.filter(recipe => 
                recipe.collection_id !== parseInt(collectionId)
            );
            
            setAllUserRecipes(availableRecipes);
        } catch (err) {
            console.error('Error fetching user recipes:', err);
            alert('Failed to load recipes. Please try again.');
        } finally {
            setLoadingRecipes(false);
        }
    };

    // Add selected recipes to collection
    const handleAddRecipesToCollection = async () => {
        if (!user || !collectionId || selectedRecipes.size === 0) return;
        
        try {
            setAddingRecipes(true);
            const token = await user.getIdToken();
            
            // Add each selected recipe to the collection
            for (const recipeId of selectedRecipes) {
                await addRecipeToCollection(collectionId, recipeId, token);
            }
            
            // Close modal and refresh the page to show updated collection
            setShowAddRecipesModal(false);
            setSelectedRecipes(new Set());
            
            // Navigate back to recipes page to refresh the collection data
            navigate('/recipes');
        } catch (err) {
            console.error('Error adding recipes to collection:', err);
            alert(`Failed to add recipes to collection: ${err.message || 'Please try again.'}`);
        } finally {
            setAddingRecipes(false);
        }
    };

    // Handle recipe selection
    const toggleRecipeSelection = (recipeId) => {
        const newSelected = new Set(selectedRecipes);
        if (newSelected.has(recipeId)) {
            newSelected.delete(recipeId);
        } else {
            newSelected.add(recipeId);
        }
        setSelectedRecipes(newSelected);
    };

    const RecipeGridCard = ({ recipe }) => {
        // Parse ingredients and instructions if they're JSON strings
        const parsedIngredients = typeof recipe.ingredients === 'string' ? 
            JSON.parse(recipe.ingredients || '[]') : (recipe.ingredients || []);
        
        // Determine display name and styling for creator/forker/transfer
        // Show stack effect for: 1) forked recipes, 2) root recipes that have forks
        const isForked = (recipe.forker_name && !recipe.is_original && !recipe.is_transfer) || 
                        (recipe.is_original && recipe.forks_count > 0);
        const isTransferred = recipe.is_transfer;
        // For root recipes with forks, show original creator name, not forker name
        const isRootWithForks = recipe.is_original && recipe.forks_count > 0;
        const displayName = isRootWithForks ? (recipe.author || recipe.creator_name || 'Unknown') : 
                           (isForked ? recipe.forker_name : (isTransferred ? recipe.creator_name : (recipe.author || recipe.creator_name || 'Unknown')));
        const nameStyle = isForked ? 'text-blue-600' : (isTransferred ? 'text-blue-600' : 'text-neutral-900 opacity-60');
        
        return (
            <div className={`relative ${isForked ? 'group w-[calc(100%-16px)] h-[calc(100%-16px)]' : ''}`}>
                {/* Stacked window effect for forked recipes - positioned under the main card */}
                {isForked && (
                    <>
                        {/* Third window (back) - full size with shadow on hover */}
                        <div className="absolute inset-0 bg-white hairline transform translate-x-3 translate-y-3 pointer-events-none group-hover:shadow-popup transition-all duration-200"></div>
                        {/* Second window (middle) - full size */}
                        <div className="absolute inset-0 bg-white hairline transform translate-x-2 translate-y-2 pointer-events-none"></div>
                        {/* First window (front) - full size */}
                        <div className="absolute inset-0 bg-white hairline transform translate-x-1 translate-y-1 pointer-events-none"></div>
                    </>
                )}
                
                {/* Main recipe card */}
                <Link 
                    to={`/recipes/${recipe.id}`}
                    className={`block aspect-square bg-neutral-0 hairline transition-all duration-200 cursor-pointer group flex flex-col relative ${isForked ? '' : 'hover:shadow-popup'}`}
                >
                    {/* Webpage browser bar for transferred recipes */}
                    {isTransferred && (
                        <div className="absolute top-0 left-0 right-0 h-6 bg-neutral-200 border-b border-neutral-900 flex items-center px-2 z-10">
                            <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-red-400 rounded-full border border-neutral-900"></div>
                                <div className="w-2 h-2 bg-yellow-400 rounded-full border border-neutral-900"></div>
                                <div className="w-2 h-2 bg-green-400 rounded-full border border-neutral-900"></div>
                            </div>
                            <div className="flex-1 mx-3">
                                <div className="h-4 bg-white border border-neutral-300 rounded-sm flex items-center px-2">
                                    <span className="text-xs text-neutral-600 truncate">{recipe.source_url ? new URL(recipe.source_url).hostname : 'recipe website'}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={`flex-1 overflow-hidden relative z-10 ${isTransferred ? 'pt-6' : ''}`}>
                        <SignedImage
                            src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                            alt={recipe.name || recipe.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            category={recipe.category}
                        />
                    </div>
                    
                    <div className="p-3 flex-shrink-0 relative z-10">
                        <Text className="font-medium text-neutral-900 mb-1 group-hover:opacity-60 transition-opacity text-sm leading-tight">
                            {recipe.name || recipe.title}
                        </Text>
                        
                        {(recipe.description || recipe.category) && (
                            <Text className="text-xs text-neutral-900 opacity-60 mb-2 line-clamp-2 leading-tight">
                                {recipe.description || recipe.category}
                            </Text>
                        )}
                        
                        <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider mb-2">
                            <span className="text-neutral-900 opacity-60">{parsedIngredients.length ? `${parsedIngredients.length} ING` : '—'}</span>
                            <span className={`${nameStyle} font-medium`}>{displayName}</span>
                        </div>
                        
                        {recipe.category && (
                            <div className="flex gap-1 flex-wrap">
                                <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                    {recipe.category}
                                </span>
                                {recipe.cooking_time && (
                                    <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                        {formatCookingTime(recipe.cooking_time)}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </Link>
            </div>
        );
    };

    const RecipeListItem = ({ recipe }) => {
        // Parse ingredients if they're JSON strings
        const parsedIngredients = typeof recipe.ingredients === 'string' ? 
            JSON.parse(recipe.ingredients || '[]') : (recipe.ingredients || []);
        
        // Determine what to show in the third column
        const isForked = recipe.forker_name && !recipe.is_original;
        const thirdColumnContent = isForked 
            ? { text: `Forked by ${recipe.forker_name}`, style: 'text-blue-600 font-medium' }
            : { text: new Date(recipe.created_at).toLocaleDateString(), style: 'text-neutral-900 opacity-60' };
            
        return (
            <Link 
                to={`/recipes/${recipe.id}`}
                className="block grid grid-cols-4 gap-4 py-2 px-0 hover:bg-neutral-50 transition-colors duration-120 cursor-pointer sm:border-none border border-neutral-200"
                style={{gridTemplateColumns: '2fr 1fr 1fr 1fr'}}
            >
                <div className="flex items-center min-w-0 pl-2">
                    <Text className="font-medium text-neutral-900 truncate">
                        {recipe.name || recipe.title}
                    </Text>
                </div>
                
                <div className="flex items-center font-mono text-sm text-neutral-900 min-w-0 pl-2">
                    <div className="flex gap-1 min-w-0">
                        {recipe.category && (
                            <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider flex-shrink-0">
                                {recipe.category}
                            </span>
                        )}
                        {recipe.cooking_time && (
                            <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider flex-shrink-0">
                                {formatCookingTime(recipe.cooking_time)}
                            </span>
                        )}
                        {!recipe.category && !recipe.cooking_time && (
                            <span className="text-neutral-900 opacity-60">—</span>
                        )}
                    </div>
                </div>
                
                <div className="flex items-center min-w-0 pl-2">
                    <Text className={`text-sm truncate ${thirdColumnContent.style}`}>
                        {thirdColumnContent.text}
                    </Text>
                </div>
                
                <div className="flex items-center justify-end pr-2">
                    <span className="text-xs font-mono text-neutral-900 opacity-60">
                        {parsedIngredients.length} ING
                    </span>
                </div>
            </Link>
        );
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-8">
                {/* Header */}
                <PageHeading 
                    size="hero"
                    subtitle={`COLLECTION`}
                >
                    {collectionName.toUpperCase()}
                </PageHeading>

                {/* Main Content */}
                <div className="bg-neutral-0 hairline minimal-padding vspace">
                    {/* Controls */}
                    <div className="space-y-6 mb-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                            <Heading level={3}>Collection Contents</Heading>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('grid')}
                                    className={`px-3 md:px-4 py-2 font-mono text-xs md:text-sm uppercase tracking-wider transition-colors ${
                                        viewMode === 'grid' 
                                            ? 'bg-neutral-900 text-neutral-0' 
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                    }`}
                                >
                                    GRID
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    className={`px-3 md:px-4 py-2 font-mono text-xs md:text-sm uppercase tracking-wider transition-colors ${
                                        viewMode === 'list' 
                                            ? 'bg-neutral-900 text-neutral-0' 
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                    }`}
                                >
                                    LIST
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 text-xs md:text-sm font-mono text-neutral-900 opacity-60">
                            <span>Showing {recipes.length} recipe{recipes.length !== 1 ? 's' : ''} in this collection</span>
                            <div className="flex items-center gap-2">
                                {/* Only show add/delete buttons for real collections, not "No Collection" */}
                                {collectionId !== 'no-collection' && (
                                    <>
                                        <button
                                            onClick={() => {
                                                setShowAddRecipesModal(true);
                                                fetchUserRecipes();
                                            }}
                                            className="px-3 py-1 bg-neutral-50 text-neutral-900 hairline uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                                        >
                                            ADD RECIPES
                                        </button>
                                        <button
                                            onClick={() => setShowDeleteModal(true)}
                                            className="px-3 py-1 bg-red-50 text-red-600 hairline uppercase tracking-wider hover:bg-red-100 transition-colors"
                                        >
                                            DELETE COLLECTION
                                        </button>
                                    </>
                                )}
                                <Link 
                                    to="/recipes"
                                    className="px-3 py-1 bg-neutral-0 text-neutral-900 hairline uppercase tracking-wider hover:bg-neutral-100 transition-colors"
                                >
                                    BACK TO RECIPES
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    {recipes.length === 0 ? (
                        <div className="text-center py-12">
                            <Text className="mb-4">This collection is empty. Add recipes to get started!</Text>
                            <Link to="/recipes" className="nav-link">BACK TO RECIPES</Link>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {recipes.map((recipe) => (
                                <RecipeGridCard key={recipe.id} recipe={recipe} />
                            ))}
                        </div>
                    ) : (
                        <div className="px-0">
                            {/* Column Headers - Hidden on smallest screens */}
                            <div className="hidden sm:grid grid-cols-4 gap-4 mb-4 px-0" style={{gridTemplateColumns: '2fr 1fr 1fr 1fr'}}>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    TITLE
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    CATEGORY
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    CREATED
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 text-right">
                                    INGREDIENTS
                                </div>
                            </div>
                            
                            {/* Recipe List */}
                            <div className="space-y-0">
                                {recipes.map((recipe) => (
                                    <RecipeListItem key={recipe.id} recipe={recipe} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Delete Collection"
            >
                <div className="vspace">
                    <Text>
                        Are you sure you want to delete the collection "{collectionName}"? This action cannot be undone.
                    </Text>
                    
                    {recipes.length > 0 && (
                        <Text className="text-sm text-neutral-900 opacity-60 mt-2">
                            Note: This will remove the collection but will not delete the {recipes.length} recipe{recipes.length !== 1 ? 's' : ''} in it. 
                            The recipes will remain in your recipe library.
                        </Text>
                    )}
                    
                    <div className="flex space-x-4 pt-4">
                        <button
                            onClick={handleDeleteCollection}
                            disabled={deleting}
                            className="px-4 py-2 bg-red-600 text-white hairline font-mono text-sm uppercase tracking-wider hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {deleting ? 'DELETING...' : 'DELETE'}
                        </button>
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            disabled={deleting}
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Add Recipes Modal */}
            <Modal
                isOpen={showAddRecipesModal}
                onClose={() => {
                    setShowAddRecipesModal(false);
                    setSelectedRecipes(new Set());
                }}
                title="Add Recipes to Collection"
            >
                <div className="vspace">
                    <Text className="mb-4">
                        Select recipes to add to "{collectionName}":
                    </Text>
                    
                    {loadingRecipes ? (
                        <div className="text-center py-8">
                            <Text>Loading recipes...</Text>
                        </div>
                    ) : allUserRecipes.length === 0 ? (
                        <div className="text-center py-8">
                            <Text className="text-neutral-900 opacity-60">
                                No recipes available to add. All your recipes are already in this collection.
                            </Text>
                        </div>
                    ) : (
                        <>
                            <div className="max-h-96 overflow-y-auto space-y-2 mb-4">
                                {allUserRecipes.map((recipe) => (
                                    <div
                                        key={recipe.id}
                                        className={`flex items-center p-3 border cursor-pointer transition-colors ${
                                            selectedRecipes.has(recipe.id)
                                                ? 'bg-neutral-900 text-neutral-0 border-neutral-900'
                                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100'
                                        }`}
                                        onClick={() => toggleRecipeSelection(recipe.id)}
                                    >
                                        <div className="flex-1">
                                            <Text className={`font-medium ${
                                                selectedRecipes.has(recipe.id) ? 'text-neutral-0' : 'text-neutral-900'
                                            }`}>
                                                {recipe.name || recipe.title}
                                            </Text>
                                            {recipe.category && (
                                                <Text className={`text-sm ${
                                                    selectedRecipes.has(recipe.id) ? 'text-neutral-0 opacity-60' : 'text-neutral-900 opacity-60'
                                                }`}>
                                                    {recipe.category}
                                                </Text>
                                            )}
                                        </div>
                                        <div className={`w-4 h-4 border-2 flex items-center justify-center ${
                                            selectedRecipes.has(recipe.id)
                                                ? 'bg-neutral-0 border-neutral-0'
                                                : 'border-neutral-900'
                                        }`}>
                                            {selectedRecipes.has(recipe.id) && (
                                                <span className="text-neutral-900 text-xs">✓</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="flex justify-between items-center pt-4">
                                <Text className="text-sm text-neutral-900 opacity-60">
                                    {selectedRecipes.size} recipe{selectedRecipes.size !== 1 ? 's' : ''} selected
                                </Text>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setShowAddRecipesModal(false);
                                            setSelectedRecipes(new Set());
                                        }}
                                        disabled={addingRecipes}
                                        className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        CANCEL
                                    </button>
                                    <button
                                        onClick={handleAddRecipesToCollection}
                                        disabled={addingRecipes || selectedRecipes.size === 0}
                                        className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {addingRecipes ? 'ADDING...' : `ADD ${selectedRecipes.size} RECIPE${selectedRecipes.size !== 1 ? 'S' : ''}`}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default CollectionDetail;
